import React, { useMemo, useCallback } from "react";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry, AllCommunityModule } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { Link, useSearchParams } from "react-router-dom";
import moment from "moment";
import { Modal } from "antd";
import { useDispatch } from "react-redux";
import { deletePublisher, syncBeehiiv } from "../../redux/publisherSlice";
import { handleMinus } from "../../utils/helper";
import { formatPrice, getCurrencySymbol } from "../../utils/currency";
import { CMS } from "../../services/constants";
import "./ag-grid-custom.css";

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

const PublisherDataTableAG = ({ publishers, loading }) => {
  const [modal, contextHolder] = Modal.useModal();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  const handleDelete = useCallback((pid) => {
    modal.confirm({
      title: "Are you sure you want to delete this publisher?",
      onOk: async () => {
        await dispatch(deletePublisher({ publisherId: pid }));
      },
    });
  }, [modal, dispatch]);

  const handleSync = useCallback((pid) => {
    modal.confirm({
      title: "Sync Beehiiv Posts",
      content: "This will trigger a sync job to fetch all posts from Beehiiv. Continue?",
      onOk: async () => {
        await dispatch(syncBeehiiv({ publisherId: pid }));
      },
    });
  }, [modal, dispatch]);

  const columnDefs = useMemo(
    () => [
      {
        field: "publisher",
        headerName: "Publisher",
        width: 150,
        pinned: "left",
        filter: "agTextColumnFilter",
        sortable: true,
        valueGetter: (params) => params.data?.name,
      },
      {
        field: "created",
        headerName: "Created",
        width: 120,
        filter: "agDateColumnFilter",
        sortable: true,
        comparator: (date1, date2) => {
          if (!date1 && !date2) return 0;
          if (!date1) return -1;
          if (!date2) return 1;
          return new Date(date1) - new Date(date2);
        },
        valueGetter: (params) => {
          return params.data?.createdAt ? new Date(params.data.createdAt) : null;
        },
        valueFormatter: (params) => {
          return params.value ? moment(params.value).format("l") : "";
        },
      },
      {
        field: "website",
        headerName: "Website",
        width: 200,
        filter: "agTextColumnFilter",
        sortable: true,
        valueGetter: (params) => params.data?.website,
      },
      {
        field: "email",
        headerName: "Email",
        width: 200,
        filter: "agTextColumnFilter",
        sortable: true,
        valueGetter: (params) => params.data?.email,
      },
      {
        field: "share",
        headerName: "Rev share",
        width: 120,
        filter: "agNumberColumnFilter",
        sortable: true,
        valueGetter: (params) => params.data?.revShare1?.percentage || 0,
        valueFormatter: (params) => params.value + "%",
      },
      {
        field: "article",
        headerName: "Article price",
        width: 130,
        filter: "agNumberColumnFilter",
        sortable: true,
        valueGetter: (params) => params.data?.articlePrice || 0,
        valueFormatter: (params) => {
          const cur = params.data?.stripe?.currency || 'usd';
          return getCurrencySymbol(cur) + formatPrice(params.value, cur);
        },
      },
      {
        field: "price",
        headerName: "Min Price",
        width: 120,
        filter: "agNumberColumnFilter",
        sortable: true,
        valueGetter: (params) => params.data?.revShare1?.minPrice || 0,
        valueFormatter: (params) => {
          if (!params.value) return "N/A";
          const cur = params.data?.stripe?.currency || 'usd';
          return getCurrencySymbol(cur) + formatPrice(params.value, cur);
        },
      },
      {
        field: "currency",
        headerName: "Currency",
        width: 120,
        filter: "agTextColumnFilter",
        sortable: true,
        valueGetter: (params) =>
          params.data?.stripe?.currency
            ? params.data?.stripe?.currency.toUpperCase()
            : "N/A",
      },
      {
        field: "sold",
        headerName: "Sold to date",
        width: 150,
        filter: "agNumberColumnFilter",
        sortable: true,
        valueGetter: (params) => params.data?.sales || 0,
        valueFormatter: (params) => {
          const cur = params.data?.stripe?.currency || 'usd';
          return getCurrencySymbol(cur) + formatPrice(params.value, cur);
        },
      },
      {
        field: "settled",
        headerName: "Settled to date",
        width: 170,
        filter: "agNumberColumnFilter",
        sortable: true,
        valueGetter: (params) => params.data?.settled || 0,
        valueFormatter: (params) => {
          const cur = params.data?.stripe?.currency || 'usd';
          return getCurrencySymbol(cur) + formatPrice(params.value, cur);
        },
      },
      {
        field: "daysSinceSettlement",
        headerName: "Days since settlement",
        width: 180,
        filter: "agNumberColumnFilter",
        sortable: true,
        valueGetter: (params) => params.data?.daysSinceSettlement ?? null,
        valueFormatter: (params) =>
          params.value === null || params.value === undefined ? "--" : params.value + "d",
      },
      {
        field: "owed",
        headerName: "Owed to publisher",
        width: 180,
        filter: "agNumberColumnFilter",
        sortable: true,
        valueGetter: (params) => {
          const balance = params.data?.balance || 0;
          // Extract numeric value from handleMinus format if needed
          return typeof balance === 'number' ? balance : parseFloat(String(balance).replace(/[^0-9.-]/g, '')) || 0;
        },
        valueFormatter: (params) => {
          const cur = params.data?.stripe?.currency || 'usd';
          return handleMinus(params.value || 0, cur);
        },
      },
      {
        field: "active",
        headerName: "Active?",
        width: 100,
        filter: "agTextColumnFilter",
        sortable: true,
        valueGetter: (params) => (params.data?.active ? "Yes" : "No"),
      },
      {
        field: "action",
        headerName: "Actions",
        width: 200,
        pinned: "right",
        filter: false,
        sortable: false,
        cellRenderer: (params) => {
          const id = params.data?._id || params.data?.id;
          const activeCMS = params.data?.activeCMS;
          const isBeehiiv = activeCMS === CMS.Beehiiv;
          if (!id) return null;
          return (
            <div style={{ whiteSpace: "normal", lineHeight: "1.5" }}>
              <Link to={`/publisher/edit/${id}`} style={{ display: "inline-block", marginRight: "8px", color: "#1677ff" }}>[edit]</Link>
              <Link to={`/settlement/settle/${id}`} style={{ display: "inline-block", marginRight: "8px", color: "#1677ff" }}>[settle]</Link>
              {isBeehiiv && (
                <span
                  className="actionBtn"
                  onClick={() => handleSync(id)}
                  style={{ cursor: "pointer", color: "#1677ff", display: "inline-block", marginRight: "8px" }}
                >
                  [sync]
                </span>
              )}
              <span
                className="actionBtn"
                onClick={() => handleDelete(id)}
                style={{ cursor: "pointer", color: "#1677ff", display: "inline-block", marginRight: "8px" }}
              >
                [delete]
              </span>
              <Link to={`/publisher/${id}`} style={{ display: "inline-block", color: "#1677ff" }}>[view]</Link>
            </div>
          );
        },
        autoHeight: true,
        wrapText: true,
      },
    ],
    [handleDelete, handleSync]
  );

  const onFirstDataRendered = useCallback((params) => {
    if (searchParams.get("owedFilter") === "positive") {
      params.api.setColumnFilterModel("owed", {
        type: "greaterThan",
        filter: 0,
      }).then(() => params.api.onFilterChanged());
    }
  }, [searchParams]);

  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      filter: true,
    }),
    []
  );

  const rowData = useMemo(() => {
    if (!publishers) return [];
    return publishers.map((pub) => {
      const daysSinceSettlement = pub.lastSettlement
        ? moment().startOf("day").diff(moment(pub.lastSettlement).startOf("day"), "days")
        : null;
      return { ...pub, daysSinceSettlement };
    });
  }, [publishers]);

  return (
    <>
      {contextHolder}
      <div
        className="ag-theme-alpine"
        style={{ height: "700px", width: "100%" }}
      >
        <AgGridReact
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          loading={loading}
          pagination={true}
          paginationPageSize={50}
          suppressMenuHide={true}
          enableCellTextSelection={true}
          ensureDomOrder={true}
          theme="legacy"
          onFirstDataRendered={onFirstDataRendered}
        />
      </div>
    </>
  );
};

export default PublisherDataTableAG;
